/* Patch & Willow sales tools: delivery estimate, cross-sell (cart drawer), cart add-ons, sticky add to cart,
   collapsible product description. */

if (!customElements.get('pw-eta')) {
  customElements.define(
    'pw-eta',
    class PwEta extends HTMLElement {
      connectedCallback() {
        const min = parseInt(this.dataset.min, 10);
        const max = parseInt(this.dataset.max, 10);
        if (Number.isNaN(min) || Number.isNaN(max)) return;
        const format = (days) => {
          const date = new Date();
          date.setDate(date.getDate() + days);
          return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        };
        this.textContent = `${format(min)} – ${format(max)}`;
      }
    }
  );
}

const pwFetchCache = new Map();
// The cart drawer re-renders on every change; keep the slider where the shopper left it.
const pwSliderMemory = { key: '', left: 0 };
let pwSliderNudged = false;

function pwFetchText(url) {
  if (!pwFetchCache.has(url)) {
    pwFetchCache.set(
      url,
      fetch(url).then((response) => {
        if (!response.ok) throw new Error(response.status);
        return response.text();
      })
    );
  }
  return pwFetchCache.get(url).catch((error) => {
    pwFetchCache.delete(url);
    throw error;
  });
}

// Prices rendered by Liquid are cents in the active currency.
function pwFormatMoney(cents) {
  const currency = window.Shopify?.currency?.active || 'USD';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(cents / 100);
}

// Shopify watches the page's fetch and reports every /cart/add as product_added_to_cart to every pixel (Meta,
// Pinterest, Google…). The default-on add-ons are added by the theme, not the shopper, so they go through a pristine
// fetch from a blank same-origin frame that Shopify does not watch. Measured on the live store 2026-10-25: one
// shopper add used to reach Meta as three AddToCart events, now one.
let pwQuietFetchFn = null;
function pwQuietFetch(url, init) {
  if (!pwQuietFetchFn) {
    try {
      const frame = document.createElement('iframe');
      frame.hidden = true;
      frame.setAttribute('aria-hidden', 'true');
      frame.tabIndex = -1;
      document.body.appendChild(frame);
      pwQuietFetchFn = frame.contentWindow.fetch.bind(frame.contentWindow);
    } catch (error) {
      pwQuietFetchFn = fetch;
    }
  }
  return pwQuietFetchFn(new URL(url, window.location.href).href, init);
}

// Posts to /cart/add.js or /cart/update.js and re-renders the cart drawer from the returned sections.
async function pwCartRequest(url, payload, variantId, source = 'pw-upsell', quiet = false) {
  const cartDrawer = document.querySelector('cart-drawer');
  const body = { ...payload };
  if (cartDrawer) {
    body.sections = cartDrawer.getSectionsToRender().map((section) => section.id);
    body.sections_url = window.location.pathname;
  }
  const response = await (quiet ? pwQuietFetch : fetch)(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  const state = await response.json();
  if (!response.ok || state.status) throw new Error(state.description || state.message || 'Error');

  if (!cartDrawer) {
    window.location = routes.cart_url;
    return;
  }
  cartDrawer.classList.remove('is-empty');
  if (source === 'pw-upsell') pwArmAddons = true;
  cartDrawer.renderContents({ id: variantId, sections: state.sections });
  if (state.item_count === 0) cartDrawer.classList.add('is-empty');
  if (typeof publish === 'function') {
    publish(PUB_SUB_EVENTS.cartUpdate, { source, productVariantId: variantId, cartData: state });
  }
}

function pwAddToCart(items) {
  return pwCartRequest(`${routes.cart_add_url}.js`, { items }, items[0].id);
}

// Add-ons switched on by default are added only right after the customer adds a product
// (the drawer opens and shows them), never silently on page load.
let pwArmAddons = false;
if (typeof subscribe === 'function') {
  // Dawn's product form publishes before it re-renders the drawer, so this runs first.
  subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
    if (event.source === 'product-form') pwArmAddons = true;
  });
}

if (!customElements.get('pw-cart-addons')) {
  customElements.define(
    'pw-cart-addons',
    class PwCartAddons extends HTMLElement {
      connectedCallback() {
        this.addEventListener('change', (event) => {
          event.stopPropagation();
          const input = event.target.closest('input[role="switch"]');
          if (input) this.toggle(input);
        });

        const armed = pwArmAddons;
        pwArmAddons = false;
        const pending = Array.from(this.querySelectorAll('input[data-auto-add]'));
        if (armed && pending.length) {
          pending.forEach((input) => (input.checked = true));
          this.run(
            () =>
              pwCartRequest(
                `${routes.cart_add_url}.js`,
                {
                  items: pending.map((input) => ({
                    id: Number(input.dataset.variantId),
                    quantity: 1,
                    properties: { _upsell: 'cart:addon-auto' },
                  })),
                },
                Number(pending[0].dataset.variantId),
                'pw-addon',
                true
              ),
            pending
          );
        }
      }

      toggle(input) {
        const id = input.dataset.variantId;
        if (input.checked) {
          this.run(
            () =>
              pwCartRequest(
                `${routes.cart_add_url}.js`,
                { items: [{ id: Number(id), quantity: 1, properties: { _upsell: 'cart:addon' } }] },
                Number(id),
                'pw-addon'
              ),
            [input]
          );
        } else {
          // Remember the opt-out so the add-on isn't switched back on with the next product.
          this.run(
            () =>
              pwCartRequest(
                `${routes.cart_update_url}.js`,
                { updates: { [input.dataset.lineKey]: 0 }, attributes: { [`_addon_off_${id}`]: 'true' } },
                null,
                'pw-addon'
              ),
            [input]
          );
        }
      }

      async run(request, inputs) {
        const switches = this.querySelectorAll('input[role="switch"]');
        switches.forEach((el) => (el.disabled = true));
        try {
          await request();
        } catch (error) {
          inputs.forEach((input) => (input.checked = !input.checked));
          switches.forEach((el) => (el.disabled = false));
          const drawerError = document.getElementById('CartDrawer-CartErrors');
          if (drawerError) drawerError.textContent = error.message;
        }
      }
    }
  );
}

if (!customElements.get('pw-addon-cleanup')) {
  customElements.define(
    'pw-addon-cleanup',
    class PwAddonCleanup extends HTMLElement {
      connectedCallback() {
        const updates = Object.fromEntries(
          this.dataset.keys
            .split(',')
            .filter(Boolean)
            .map((key) => [key, 0])
        );
        pwCartRequest(`${routes.cart_update_url}.js`, { updates }, null, 'pw-addon').catch(() => {});
      }
    }
  );
}

if (!customElements.get('pw-upsell')) {
  customElements.define(
    'pw-upsell',
    class PwUpsell extends HTMLElement {
      connectedCallback() {
        this.mode = this.dataset.mode;
        this.limit = parseInt(this.dataset.limit, 10) || 4;
        this.list = this.querySelector('[data-list]');
        this.exclude = (this.dataset.exclude || '').split(',').filter(Boolean);
        this.leadTypes = (this.dataset.leadTypes || '').split(',').filter(Boolean);
        // type keys from the theme settings "Suggestion order" / "Don't suggest again" (snippets/pw-upsell-keys.liquid)
        this.order = (this.dataset.order || '').split(',').filter(Boolean);
        this.once = (this.dataset.once || '').split(',').filter(Boolean);
        try {
          this.added = JSON.parse(this.dataset.added || '{}');
        } catch (error) {
          this.added = {};
        }

        // cart-drawer-items listens for "change" to update quantities; keep ours away from it.
        this.addEventListener('change', (event) => {
          event.stopPropagation();
          this.onChange(event);
        });
        this.addEventListener('click', this.onClick.bind(this));
        if (this.mode === 'drawer') {
          this.list.addEventListener('scroll', () => this.updateNav(true), { passive: true });
        }

        const deferred = this.querySelector('template[data-deferred-cards]');
        if (deferred) {
          this.whenDrawerOpens(() => {
            this.list.append(deferred.content.cloneNode(true));
            deferred.remove();
            this.prune();
            this.reveal();
          });
          return;
        }

        this.prune();
        if (this.hasAttribute('data-fetch')) {
          this.load();
        } else {
          this.reveal();
        }
      }

      whenDrawerOpens(callback) {
        const drawer = this.closest('cart-drawer');
        if (!drawer || drawer.classList.contains('active')) {
          callback();
          return;
        }
        const observer = new MutationObserver(() => {
          if (!drawer.classList.contains('active')) return;
          observer.disconnect();
          callback();
        });
        observer.observe(drawer, { attributes: true, attributeFilter: ['class'] });
      }

      cards() {
        return Array.from(this.list.querySelectorAll('.pw-up-card'));
      }

      skip(card, seen) {
        const id = card.dataset.productId;
        return (
          seen.has(id) ||
          this.exclude.includes(id) ||
          // e.g. no second bedding set once the cart has one (unless the shopper added it from here)
          (this.once.includes(card.dataset.type) && this.leadTypes.includes(card.dataset.type) && !this.added[id])
        );
      }

      prune() {
        const seen = new Set();
        this.cards().forEach((card) => {
          if (this.skip(card, seen)) card.remove();
          seen.add(card.dataset.productId);
        });
        this.cards()
          .slice(this.limit)
          .forEach((card) => card.remove());
      }

      async load() {
        const design = this.dataset.design;
        const styleHandle = this.dataset.styleHandle;
        const productId = this.dataset.productId;
        // Each step is tried in order until the list is full; a failing step does not stop the next one.
        const steps = [];
        // 1. same design (pw-upsell section; matches patch.design_key, else the title's design name)
        if (design) {
          steps.push(`${routes.search_url}?q=${encodeURIComponent(design)}&type=product&options%5Bfields%5D=title&section_id=pw-upsell`);
        }
        if (this.mode === 'drawer') {
          // 2. same style: the style collection (pw-upsell-any section)
          if (styleHandle) steps.push(`${routes.collections_url}/${encodeURIComponent(styleHandle)}?section_id=pw-upsell-any`);
          // 3. Shopify's related products (pw-upsell-any section)
          if (productId) {
            steps.push(
              `${routes.product_recommendations_url}?product_id=${productId}&limit=10&intent=related&section_id=pw-upsell-any`
            );
          }
        }
        for (const url of steps) {
          if (this.cards().length >= this.limit) break;
          try {
            await this.append(url);
          } catch (error) {
            console.warn('pw-upsell', error);
          }
        }
        this.sortCards();
        // the heading only switches to "You may also like" when no same-design card was found
        const sameDesign = this.cards().some((card) => !['style', 'related'].includes(card.dataset.source));
        const heading = this.querySelector('[data-heading]');
        if (heading && !sameDesign && this.dataset.relatedHeading) heading.textContent = this.dataset.relatedHeading;
        this.reveal();
      }

      // Same-design pieces first, then style-collection pieces, then related products; within a tier the
      // "Suggestion order" with types the cart already has last.
      sortCards() {
        const tier = (card) => {
          if (card.dataset.source === 'related') return 2000;
          if (card.dataset.source === 'style') return 1000;
          return 0;
        };
        const rank = (card) => {
          const type = card.dataset.type || 'other';
          const order = this.order.indexOf(type);
          return (
            tier(card) +
            (this.leadTypes.includes(type) ? 100 : 0) +
            (order === -1 ? this.order.length : order)
          );
        };
        this.cards()
          .map((card, index) => ({ card, index, rank: rank(card) }))
          .sort((a, b) => a.rank - b.rank || a.index - b.index)
          .forEach(({ card }) => this.list.append(card));
      }

      // Adds cards from a pw-upsell section response that aren't already shown, up to the limit.
      async append(url) {
        const html = await pwFetchText(url);
        const results = new DOMParser().parseFromString(html, 'text/html').querySelector('.pw-upsell-results');
        if (!results) return;
        const seen = new Set(this.cards().map((card) => card.dataset.productId));
        for (const card of results.querySelectorAll('.pw-up-card')) {
          if (this.cards().length >= this.limit) break;
          if (this.skip(card, seen)) continue;
          seen.add(card.dataset.productId);
          this.list.append(card);
        }
      }

      reveal() {
        const hasCards = this.cards().length > 0;
        this.hidden = !hasCards;
        if (!hasCards) return;
        this.applyAdded();
        this.shortenTitles();
        const key = `${this.dataset.design}|${this.dataset.productId}`;
        if (pwSliderMemory.key === key) this.list.scrollLeft = pwSliderMemory.left;
        pwSliderMemory.key = key;
        this.updateNav();
        this.nudge();
      }

      // Same-design cards repeat the design name; show only the product type ("Fabric Lamp").
      shortenTitles() {
        const design = (this.dataset.design || '').trim();
        if (!design) return;
        this.cards().forEach((card) => {
          const title = card.querySelector('.pw-up-title');
          const text = title.textContent.trim();
          if (text.toLowerCase().startsWith(design.toLowerCase() + ' ')) {
            title.setAttribute('title', text);
            title.textContent = text.slice(design.length).trim();
          }
        });
      }

      // Products added from the slider stay in it, ticked, with the variant that is in the cart.
      applyAdded() {
        this.cards().forEach((card) => {
          const line = this.added[card.dataset.productId];
          card.classList.toggle('is-added', !!line);
          card.querySelectorAll('[data-add]').forEach((button) => button.setAttribute('aria-pressed', line ? 'true' : 'false'));
          const select = card.querySelector('select[data-variant]');
          if (line) {
            card.dataset.lineKey = line.key;
            if (select) {
              select.value = String(line.variant);
              select.disabled = true;
              this.syncPrice(card, select);
            }
          }
        });
      }

      updateNav(remember = false) {
        const { scrollLeft, scrollWidth, clientWidth } = this.list;
        const atStart = scrollLeft <= 4;
        const atEnd = scrollLeft + clientWidth >= scrollWidth - 4;
        this.classList.toggle('is-start', atStart);
        this.classList.toggle('is-end', atEnd);
        this.classList.toggle('has-overflow', scrollWidth > clientWidth + 4);
        this.querySelectorAll('[data-dir]').forEach((button) => {
          button.disabled = button.dataset.dir === '-1' ? atStart : atEnd;
        });
        if (remember) pwSliderMemory.left = scrollLeft;
      }

      // Once per page: slide a little and back, so shoppers see the row scrolls sideways.
      nudge() {
        if (pwSliderNudged || !this.classList.contains('has-overflow') || this.list.scrollLeft > 0) return;
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
        pwSliderNudged = true;
        setTimeout(() => {
          if (!this.isConnected || !this.offsetParent) {
            pwSliderNudged = false;
            return;
          }
          this.list.scrollTo({ left: 72, behavior: 'smooth' });
          setTimeout(() => this.isConnected && this.list.scrollTo({ left: 0, behavior: 'smooth' }), 650);
        }, 900);
      }

      syncPrice(card, select) {
        const option = select.selectedOptions[0];
        if (!option) return;
        card.querySelector('[data-now]').textContent = option.dataset.now;
        card.querySelector('[data-was]').textContent = option.dataset.was;
      }

      onChange(event) {
        const card = event.target.closest('.pw-up-card');
        if (!card) return;
        if (event.target.matches('select[data-variant]')) this.syncPrice(card, event.target);
      }

      onClick(event) {
        const arrow = event.target.closest('[data-dir]');
        if (arrow) {
          const card = this.cards()[0];
          const step = card ? card.getBoundingClientRect().width + 8 : this.list.clientWidth * 0.8;
          this.list.scrollBy({ left: Number(arrow.dataset.dir) * step, behavior: 'smooth' });
          return;
        }
        const add = event.target.closest('[data-add]');
        if (add && this.mode === 'drawer') {
          this.toggleCard(add.closest('.pw-up-card'));
          return;
        }
      }

      async toggleCard(card) {
        if (card.classList.contains('is-loading')) return;
        card.classList.add('is-loading');
        try {
          if (card.dataset.lineKey) {
            await pwCartRequest(
              `${routes.cart_update_url}.js`,
              { updates: { [card.dataset.lineKey]: 0 } },
              null,
              'pw-upsell-remove'
            );
          } else {
            await pwAddToCart([this.lineFor(card)]);
          }
        } catch (error) {
          card.classList.remove('is-loading');
          this.showError(error.message);
        }
      }

      lineFor(card) {
        const input = card.querySelector('[data-variant]');
        return {
          id: Number(input.value),
          quantity: 1,
          properties: { _upsell: `cart:${card.dataset.source}` },
        };
      }

      showError(message) {
        const error = this.querySelector('[data-error]');
        if (error) {
          error.textContent = message;
          error.hidden = false;
        } else {
          const drawerError = document.getElementById('CartDrawer-CartErrors');
          if (drawerError) drawerError.textContent = message;
        }
      }
    }
  );
}

if (!customElements.get('pw-sticky-atc')) {
  customElements.define(
    'pw-sticky-atc',
    class PwStickyAtc extends HTMLElement {
      connectedCallback() {
        if (this.closest('quick-add-modal')) return;
        const sectionId = this.dataset.sectionId;
        this.form = document.getElementById(`product-form-${sectionId}`);
        this.mainButton = document.getElementById(`ProductSubmitButton-${sectionId}`);
        this.priceSource = document.getElementById(`price-${sectionId}`);
        this.button = this.querySelector('[data-submit]');
        if (!this.form || !this.mainButton) return;

        this.syncPrice();
        if (this.priceSource) {
          new MutationObserver(() => this.syncPrice()).observe(this.priceSource, { childList: true, subtree: true });
        }
        new MutationObserver(() => this.syncButton()).observe(this.mainButton, {
          attributes: true,
          childList: true,
          subtree: true,
        });
        this.syncButton();

        this.button.addEventListener('click', () => {
          if (this.mainButton.disabled) {
            this.mainButton.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return;
          }
          this.mainButton.click();
        });

        this.optionSelects = Array.from(this.querySelectorAll('select[data-option-index]'));
        this.optionSelects.forEach((select) =>
          select.addEventListener('change', () => this.chooseOption(Number(select.dataset.optionIndex), select.value))
        );
        if (this.optionSelects.length) {
          this.syncOptions();
          if (typeof subscribe === 'function') {
            subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
              if (event.data?.sectionId === sectionId) this.syncOptions();
            });
          }
        }

        this.hidden = false;
        // Pad each dropdown's text past its option-name label ("Size", "Color"...)
        this.optionSelects.forEach((select) => {
          const name = select.parentElement.querySelector('.pw-sticky__option-name');
          if (name) select.parentElement.style.setProperty('--pw-option-name-width', `${name.offsetWidth + 8}px`);
        });
        // A scroll check rather than IntersectionObserver: a fast fling can jump past the button
        // without it ever intersecting, and the observer would then never fire.
        let ticking = false;
        const update = () => {
          ticking = false;
          const passed = this.mainButton.getBoundingClientRect().bottom < 0;
          if (passed === this.classList.contains('is-visible')) return;
          this.classList.toggle('is-visible', passed);
          document.body.classList.toggle('pw-sticky-on', passed);
          // Floating widgets (fixed to the bottom of the screen) sit this far up so they never cover the button
          if (passed) document.documentElement.style.setProperty('--pw-sticky-h', `${this.offsetHeight}px`);
        };
        window.addEventListener(
          'scroll',
          () => {
            if (ticking) return;
            ticking = true;
            requestAnimationFrame(update);
          },
          { passive: true }
        );
        update();
      }

      pickerField(index) {
        const picker = document.getElementById(`variant-selects-${this.dataset.sectionId}`);
        return picker?.querySelectorAll('.product-form__input')[index];
      }

      // Mirrors the main picker: selected value per option, and which values are sold out with the others.
      syncOptions() {
        this.optionSelects.forEach((select) => {
          const field = this.pickerField(Number(select.dataset.optionIndex));
          if (!field) return;
          const radios = Array.from(field.querySelectorAll('input[type="radio"]'));
          const dropdown = field.querySelector('select');
          const current = radios.find((radio) => radio.checked)?.value ?? dropdown?.value;
          if (current !== undefined) select.value = current;
          Array.from(select.options).forEach((option) => {
            const radio = radios.find((input) => input.value === option.value);
            const soldOut = radio ? radio.classList.contains('disabled') : false;
            option.textContent = soldOut ? `${option.value} – ${window.variantStrings?.soldOut || 'Sold out'}` : option.value;
          });
        });
      }

      async chooseOption(index, value) {
        const field = this.pickerField(index);
        if (!field) return;
        const radio = Array.from(field.querySelectorAll('input[type="radio"]')).find((input) => input.value === value);
        const dropdown = field.querySelector('select');
        // After a change Dawn focuses the picker input and scrolls to the variant's image, which would
        // hide this bar; keep the shopper where they are.
        const releaseScroll = this.holdScroll();
        this.classList.add('is-busy');
        try {
          const changed = this.nextVariantChange();
          if (radio && !radio.checked) radio.click();
          else if (!radio && dropdown && dropdown.value !== value) {
            dropdown.value = value;
            dropdown.dispatchEvent(new Event('change', { bubbles: true }));
          }
          await changed;
        } finally {
          this.classList.remove('is-busy');
          setTimeout(releaseScroll, 800);
        }
      }

      holdScroll() {
        const y = window.scrollY;
        const scrollTo = window.scrollTo;
        const focus = HTMLElement.prototype.focus;
        const scrollIntoView = Element.prototype.scrollIntoView;
        window.scrollTo = () => {};
        Element.prototype.scrollIntoView = () => {};
        HTMLElement.prototype.focus = function (options) {
          return focus.call(this, { ...options, preventScroll: true });
        };
        return () => {
          window.scrollTo = scrollTo;
          Element.prototype.scrollIntoView = scrollIntoView;
          HTMLElement.prototype.focus = focus;
          if (Math.abs(window.scrollY - y) > 2) window.scrollTo(0, y);
        };
      }

      nextVariantChange() {
        return new Promise((resolve) => {
          if (typeof subscribe !== 'function') return setTimeout(resolve, 1200);
          const timer = setTimeout(() => {
            unsubscribe();
            resolve();
          }, 4000);
          const unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, () => {
            clearTimeout(timer);
            unsubscribe();
            resolve();
          });
        });
      }

      syncPrice() {
        const target = this.querySelector('[data-price]');
        const sale = this.priceSource?.querySelector('.price');
        if (!sale) return;
        const clone = sale.cloneNode(true);
        clone.querySelectorAll('.price__badge-sold-out, .unit-price, [id]').forEach((node) => node.removeAttribute('id'));
        clone.querySelector('.price__badge-sold-out')?.remove();
        target.replaceChildren(clone);
      }

      syncButton() {
        const label = this.mainButton.querySelector('span')?.textContent.trim();
        if (label) this.button.textContent = label;
        this.button.classList.toggle('is-disabled', this.mainButton.disabled);
      }
    }
  );
}

// Long product descriptions start collapsed so shoppers reach related products sooner.
if (!customElements.get('pw-read-more')) {
  customElements.define(
    'pw-read-more',
    class PwReadMore extends HTMLElement {
      connectedCallback() {
        this.content = this.querySelector('[data-content]');
        this.toggleButton = this.querySelector('[data-toggle]');
        const collapsedHeight = parseFloat(getComputedStyle(this).getPropertyValue('--pw-collapsed-height')) || 260;
        if (this.content.scrollHeight <= collapsedHeight + 80) return;
        this.classList.add('is-collapsible', 'is-collapsed');
        this.toggleButton.hidden = false;
        this.toggleButton.addEventListener('click', () => {
          const collapsed = this.classList.toggle('is-collapsed');
          this.toggleButton.setAttribute('aria-expanded', String(!collapsed));
          this.toggleButton.querySelector('[data-more]').hidden = !collapsed;
          this.toggleButton.querySelector('[data-less]').hidden = collapsed;
          if (collapsed) this.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      }
    }
  );
}

// On phones the header search icon opens the menu drawer with its search field focused, instead of Dawn's
// search modal. The field is focused within the tap (phones raise the keyboard only then), so the drawer is
// opened right away rather than after Dawn's timeout (see assets/pw.css).
document.addEventListener(
  'click',
  (event) => {
    // Dawn's drawer treats every button inside it as a submenu "back" button; the search field's clear
    // button only clears the field.
    if (event.target.closest('.pw-mnav__reset')) {
      event.stopPropagation();
      return;
    }

    if (!event.target.closest('.header__search summary')) return;
    const menuToggle = document.querySelector('header-drawer > details > summary');
    const field = document.getElementById('PwMenuSearch');
    if (!menuToggle || !field || !menuToggle.getClientRects().length) return;

    event.preventDefault();
    event.stopPropagation();
    const drawer = menuToggle.parentNode;
    if (drawer.open) {
      // Close any open submenu at once; Dawn's animated close would move focus back to its card afterwards.
      drawer.querySelectorAll('details[open]').forEach((submenu) => {
        submenu.removeAttribute('open');
        submenu.classList.remove('menu-opening');
        submenu.querySelector('summary').setAttribute('aria-expanded', false);
      });
      drawer.querySelectorAll('.submenu-open').forEach((menu) => menu.classList.remove('submenu-open'));
    } else {
      menuToggle.click();
      drawer.classList.add('menu-opening');
    }
    field.focus({ preventScroll: true });
  },
  true
);

// "Complete the room" (sections/pw-room-set.liquid): tick pieces, choose sizes, add the set in one go.
// Ticked pieces without a size count at their lowest price and the total reads "from ...".
if (!customElements.get('pw-room-set')) {
  customElements.define(
    'pw-room-set',
    class PwRoomSet extends HTMLElement {
      connectedCallback() {
        this.items = [...this.querySelectorAll('[data-item]')];
        this.button = this.querySelector('[data-add]');
        this.error = this.querySelector('[data-error]');
        this.sizeError = this.error.textContent;
        this.addEventListener('change', (event) => {
          const item = event.target.closest('[data-item]');
          if (!item) return;
          // Choosing a size ticks the piece
          if (event.target.matches('select[data-variant]') && event.target.value) {
            item.querySelector('[data-check]').checked = true;
          }
          item.classList.toggle('is-off', !item.querySelector('[data-check]').checked);
          item.classList.remove('needs-size');
          this.update();
        });
        this.button.addEventListener('click', () => this.add());
        this.update();
      }

      ticked() {
        return this.items.filter((item) => item.querySelector('[data-check]').checked);
      }

      chosenCents(item) {
        const input = item.querySelector('[data-variant]');
        const option = input.selectedOptions ? input.selectedOptions[0] : input;
        return parseInt(option.dataset.cents, 10) || 0;
      }

      update() {
        let total = 0;
        let estimated = false;
        this.items.forEach((item) => {
          const price = item.querySelector('[data-price]');
          const cents = this.chosenCents(item);
          price.textContent = cents ? pwFormatMoney(cents) : price.dataset.from;
          if (!item.querySelector('[data-check]').checked) return;
          if (cents) {
            total += cents;
          } else {
            total += parseInt(price.dataset.min, 10) || 0;
            estimated = true;
          }
        });
        const count = this.ticked().length;
        // A function replacement, so "$1" in a price like "$110.01" isn't read as a pattern group
        const text = (name, value) => (this.dataset[name] || '').replace(/\[(count|amount|price)\]/, () => value);
        this.querySelector('[data-count]').textContent = count === 1 ? text('totalOne') : text('totalOther', count);
        this.querySelector('[data-total]').textContent = estimated
          ? text('fromTotal', pwFormatMoney(total))
          : pwFormatMoney(total);
        if (count === 0) this.button.textContent = text('addNone');
        else this.button.textContent = count === 1 ? text('addOne') : text('addOther', count);
        this.button.disabled = count === 0;

        const threshold = parseInt(this.dataset.threshold, 10) || 0;
        const ship = this.querySelector('[data-ship]');
        if (threshold && ship) {
          this.querySelector('[data-bar]').style.width = `${Math.min(100, (total / threshold) * 100)}%`;
          ship.innerHTML =
            total >= threshold ? text('shipUnlocked') : text('shipAway', pwFormatMoney(threshold - total));
        }
        this.error.hidden = true;
      }

      async add() {
        const lines = [];
        let missing = null;
        this.ticked().forEach((item) => {
          const input = item.querySelector('[data-variant]');
          if (!input.value) {
            item.classList.add('needs-size');
            missing = missing || input;
            return;
          }
          lines.push({ id: parseInt(input.value, 10), quantity: 1, properties: { _upsell: this.dataset.source } });
        });
        if (missing) {
          this.error.textContent = this.sizeError;
          this.error.hidden = false;
          missing.focus();
          return;
        }
        if (!lines.length) return;
        this.button.setAttribute('aria-busy', 'true');
        this.button.disabled = true;
        try {
          await pwAddToCart(lines);
        } catch (error) {
          this.error.textContent = error.message;
          this.error.hidden = false;
        } finally {
          this.button.removeAttribute('aria-busy');
          this.button.disabled = false;
        }
      }
    }
  );
}

// Footer link groups fold on phones (sections/footer.liquid) and stay open on larger screens.
const pwFooterWide = window.matchMedia('(min-width: 750px)');
function pwFoldFooter() {
  document.querySelectorAll('details.pw-facc').forEach((details) => {
    details.open = pwFooterWide.matches;
  });
}
pwFoldFooter();
pwFooterWide.addEventListener('change', pwFoldFooter);

// Christmas effect (.pw-xmas: home season banner and menu banner): canvas snow in three depths and a sleigh
// crossing now and then with a sparkle trail. Runs only while the banner is on screen; reduced motion gets still snow.
class PwXmasFx {
  constructor(banner) {
    this.banner = banner;
    this.canvas = banner.querySelector('.pw-xmas__fx');
    this.ctx = this.canvas.getContext('2d');
    this.sleigh = banner.querySelector('.pw-xmas__sleigh');
    this.still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.sprites = { soft: this.softSprite(), crystal: this.crystalSprite(), spark: this.sparkSprite() };
    this.sparks = [];
    this.sparkDebt = 0;
    this.t = 0;
    new ResizeObserver(() => this.resize()).observe(banner);
  }

  sprite(size, paint) {
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = size;
    paint(canvas.getContext('2d'), size / 2);
    return canvas;
  }

  softSprite() {
    return this.sprite(32, (ctx, c) => {
      const glow = ctx.createRadialGradient(c, c, 0, c, c, c);
      glow.addColorStop(0, 'rgba(255,255,255,1)');
      glow.addColorStop(0.35, 'rgba(255,255,255,0.85)');
      glow.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.fillStyle = glow;
      ctx.fillRect(0, 0, c * 2, c * 2);
    });
  }

  crystalSprite() {
    return this.sprite(48, (ctx, c) => {
      ctx.strokeStyle = '#fff';
      ctx.lineCap = 'round';
      ctx.lineWidth = 2.2;
      ctx.shadowColor = 'rgba(255,255,255,0.9)';
      ctx.shadowBlur = 4;
      ctx.translate(c, c);
      for (let arm = 0; arm < 6; arm++) {
        ctx.rotate(Math.PI / 3);
        ctx.beginPath();
        ctx.moveTo(0, 0);
        ctx.lineTo(0, -18);
        ctx.moveTo(0, -10);
        ctx.lineTo(-5, -15);
        ctx.moveTo(0, -10);
        ctx.lineTo(5, -15);
        ctx.stroke();
      }
    });
  }

  sparkSprite() {
    return this.sprite(24, (ctx, c) => {
      const glow = ctx.createRadialGradient(c, c, 0, c, c, c);
      glow.addColorStop(0, 'rgba(255,240,180,1)');
      glow.addColorStop(0.3, 'rgba(255,205,80,0.7)');
      glow.addColorStop(1, 'rgba(255,190,40,0)');
      ctx.fillStyle = glow;
      ctx.fillRect(0, 0, c * 2, c * 2);
      ctx.fillStyle = '#fff6d0';
      ctx.beginPath();
      ctx.moveTo(c, 2);
      ctx.lineTo(c + 2, c - 2);
      ctx.lineTo(c * 2 - 2, c);
      ctx.lineTo(c + 2, c + 2);
      ctx.lineTo(c, c * 2 - 2);
      ctx.lineTo(c - 2, c + 2);
      ctx.lineTo(2, c);
      ctx.lineTo(c - 2, c - 2);
      ctx.fill();
    });
  }

  resize() {
    const ratio = Math.min(window.devicePixelRatio || 1, 2);
    this.w = this.banner.clientWidth;
    this.h = this.banner.clientHeight;
    this.canvas.width = Math.round(this.w * ratio);
    this.canvas.height = Math.round(this.h * ratio);
    this.ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    const count = Math.min(170, Math.round((this.w * this.h) / 1400));
    this.flakes = Array.from({ length: count }, () => this.flake(true));
    this.draw();
  }

  flake(anywhere) {
    const depth = Math.random();
    let r = 0.8 + Math.random() * 1.2;
    if (depth > 0.88) r = 3.8 + Math.random() * 3.2;
    else if (depth > 0.55) r = 2 + Math.random() * 1.6;
    return {
      x: Math.random() * this.w,
      y: anywhere ? Math.random() * this.h : -12,
      r,
      depth,
      fall: 16 + r * 10 + Math.random() * 10,
      sway: 6 + Math.random() * 16,
      phase: Math.random() * 6.3,
      spin: Math.random() * 6.3,
      crystal: r > 4.4,
      alpha: 0.55 + depth * 0.45,
    };
  }

  start() {
    if (this.running || this.still) return;
    this.running = true;
    this.last = performance.now();
    requestAnimationFrame((now) => this.tick(now));
  }

  stop() {
    this.running = false;
  }

  tick(now) {
    if (!this.running) return;
    const dt = Math.min(0.05, (now - this.last) / 1000);
    this.last = now;
    this.t += dt;
    this.step(dt);
    this.draw();
    requestAnimationFrame((next) => this.tick(next));
  }

  step(dt) {
    const wind = Math.sin(this.t * 0.35) * 14;
    this.flakes.forEach((f) => {
      f.y += f.fall * dt;
      f.phase += dt * 1.3;
      f.spin += dt * 0.9;
      f.x += (wind * (0.4 + f.depth) + Math.cos(f.phase) * f.sway) * dt;
      if (f.y - f.r * 3 > this.h) Object.assign(f, this.flake(false));
      if (f.x < -12) f.x += this.w + 24;
      else if (f.x > this.w + 12) f.x -= this.w + 24;
    });
    this.flySleigh(dt);
    this.sparks = this.sparks.filter((spark) => (spark.life -= dt) > 0);
    this.sparks.forEach((spark) => {
      spark.x += spark.vx * dt;
      spark.y += spark.vy * dt;
      spark.vy += 26 * dt;
    });
  }

  flySleigh(dt) {
    if (!this.sleigh) return;
    const delay = 1.2;
    const flight = 7;
    const local = (this.t - delay) % (flight + 5);
    if (this.t < delay || local > flight) {
      this.sleigh.style.opacity = 0;
      return;
    }
    const p = local / flight;
    const sw = this.sleigh.offsetWidth;
    const sh = this.sleigh.offsetHeight;
    // the silhouette faces left: fly right to left, nose up at the start and down at the end
    const x = this.w - p * (this.w + sw);
    const y = this.h * 0.03 - Math.sin(p * Math.PI) * this.h * 0.04 + Math.sin(this.t * 4) * 1.5;
    const tilt = Math.cos(p * Math.PI) * 3;
    this.sleigh.style.opacity = 1;
    this.sleigh.style.transform = `translate3d(${x}px, ${y}px, 0) rotate(${tilt}deg)`;
    this.sparkDebt += dt * 45;
    while (this.sparkDebt >= 1) {
      this.sparkDebt -= 1;
      this.sparks.push({
        x: x + sw * 0.92 + Math.random() * 6,
        y: y + sh * 0.85 + (Math.random() - 0.5) * 8,
        vx: 20 + Math.random() * 30,
        vy: -10 + Math.random() * 20,
        size: 5 + Math.random() * 7,
        life: 0.8 + Math.random() * 0.8,
        max: 1.6,
      });
    }
  }

  draw() {
    const { ctx } = this;
    ctx.clearRect(0, 0, this.w, this.h);
    this.flakes.forEach((f) => {
      ctx.globalAlpha = f.alpha;
      if (f.crystal) {
        const size = f.r * 4;
        ctx.save();
        ctx.translate(f.x, f.y);
        ctx.rotate(f.spin);
        ctx.drawImage(this.sprites.crystal, -size / 2, -size / 2, size, size);
        ctx.restore();
      } else {
        const size = f.r * 3;
        ctx.drawImage(this.sprites.soft, f.x - size / 2, f.y - size / 2, size, size);
      }
    });
    this.sparks.forEach((spark) => {
      ctx.globalAlpha = Math.min(1, spark.life / (spark.max * 0.6));
      ctx.drawImage(this.sprites.spark, spark.x - spark.size / 2, spark.y - spark.size / 2, spark.size, spark.size);
    });
    ctx.globalAlpha = 1;
  }
}

if ('IntersectionObserver' in window) {
  const pwBannerObserver = new IntersectionObserver((entries) =>
    entries.forEach((entry) => {
      entry.target.classList.toggle('is-playing', entry.isIntersecting);
      if (entry.isIntersecting) entry.target.pwFx?.start();
      else entry.target.pwFx?.stop();
    })
  );
  const pwWatchBanners = () =>
    document.querySelectorAll('.pw-xmas').forEach((banner) => {
      if (!banner.pwFx && banner.querySelector('.pw-xmas__fx')) banner.pwFx = new PwXmasFx(banner);
      pwBannerObserver.observe(banner);
    });
  pwWatchBanners();
  document.addEventListener('shopify:section:load', pwWatchBanners);
}

// Collection circles (sections/pw-theme-circles.liquid): the first time a scrollable row comes into view it scrolls
// a little way and back, so shoppers see it moves sideways. Real scrolling (not a transform) keeps the end smooth;
// snapping is paused meanwhile, and touching the row stops it.
if ('IntersectionObserver' in window && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
  const pwNudgeObserver = new IntersectionObserver(
    (entries, observer) =>
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const list = entry.target;
        observer.unobserve(list);
        if (list.scrollWidth <= list.clientWidth + 8) return;
        const timers = [];
        const restore = () => {
          timers.forEach(clearTimeout);
          list.style.removeProperty('scroll-snap-type');
        };
        list.style.scrollSnapType = 'none';
        timers.push(setTimeout(() => list.scrollTo({ left: 80, behavior: 'smooth' }), 200));
        timers.push(setTimeout(() => list.scrollTo({ left: 0, behavior: 'smooth' }), 1000));
        timers.push(setTimeout(restore, 1900));
        list.addEventListener('pointerdown', restore, { once: true });
      }),
    { threshold: 0.6 }
  );
  document.querySelectorAll('.pw-circles__list').forEach((list) => pwNudgeObserver.observe(list));
}
